import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CardActions,
  Divider,
  Alert,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  Tooltip,
  Avatar,
  InputAdornment
} from "@mui/material";
import {
  Edit,
  Autorenew,
  PersonAdd,
  Phone,
  Class as ClassIcon,
  CalendarToday,
  Visibility,
  Person,
  Email,
  Lock,
  Numbers,
  School,
  ChevronRight,
  Warning,
  CheckCircle,
  AccessTime
} from "@mui/icons-material";

import { AuthContext } from "../store/AuthContext";
import { getStudents, updateStudent } from "../api/studentsApi";
import { getClasses, getSubjects } from "../api/classSubjectApi";

// ---------- DATE HELPERS ----------
const parseDate = (dateStr) => {
  if (!dateStr || dateStr === "0000-00-00") return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
};

const formatDate = (dt) => {
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
};

const isExpired = (expiryDateStr) => {
  if (!expiryDateStr) return true;
  const expiry = parseDate(expiryDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return !expiry || expiry < today;
};

const addMonthsForRenew = (expiryDateStr, monthsToAdd) => {
  const today = new Date();
  const todayMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  let baseDate = parseDate(expiryDateStr);
  if (!baseDate || baseDate < todayMidnight) {
    baseDate = todayMidnight;
  }
  baseDate.setMonth(baseDate.getMonth() + monthsToAdd);
  return formatDate(baseDate);
};

export default function TeacherStudents() {
  const { user, token } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editStudentData, setEditStudentData] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    class_id: "",
    content: 0,
  });

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewStudentData, setRenewStudentData] = useState(null);
  const [renewMonths, setRenewMonths] = useState(1);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    phone: "",
    class_id: "",
    content: 0,
    password: ""
  });

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getStudents(token);
      setStudents(res.data);
    } catch (err) {
      console.error("Error loading students", err);
      setError("Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  const loadClassAndSubject = async () => {
    try {
      const classRes = await getClasses(token);
      const subjectRes = await getSubjects(token);
      setClasses(classRes.data);
      setSubjects(subjectRes.data);
    } catch (err) {
      console.error("Error loading class/subject", err);
    }
  };

  useEffect(() => {
    loadStudents();
    loadClassAndSubject();
  }, []);

  const handleOpenEdit = (student) => {
    setEditStudentData(student);
    setEditForm({
      name: student.name || "",
      phone: student.phone || "",
      class_id: student.class_id || "",
      content: student.content || 0,
    });
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditStudentData(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ 
      ...prev, 
      [name]: name === 'content' || name === 'class_id' ? Number(value) : value 
    }));
  };

  const handleEditSubmit = async () => {
    if (!editStudentData) return;
    try {
      await updateStudent(token, editStudentData.id, {
        name: editForm.name,
        phone: editForm.phone,
        class_id: editForm.class_id,
        content: editForm.content,
      });
      await loadStudents();
      handleCloseEdit();
    } catch (err) {
      console.error("Error updating student", err);
      setError("Failed to update student");
    }
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({
      ...prev,
      [name]: name === 'content' || name === 'class_id' ? Number(value) : value
    }));
  };

  const handleAddStudent = async () => {
    try {
      const payload = {
        name: addForm.name,
        phone: addForm.phone,
        class_id: addForm.class_id,
        content: addForm.content,
        teacher_id: user.id,
        password: addForm.password,
        expiryDate: getYesterday(),
      };

      await axios.post(
        "https://lms.childinfo.in/api/students.php",
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert("Student added successfully");
      setAddOpen(false);
      setAddForm({
        name: "",
        phone: "",
        class_id: "",
        content: 0,
        password: ""
      });
      loadStudents();
    } catch (err) {
      console.error(err);
      setError("Failed to add student");
    }
  };

  const handleOpenRenew = (student) => {
    setRenewStudentData(student);
    setRenewMonths(1);
    setRenewOpen(true);
  };

  const handleCloseRenew = () => {
    setRenewOpen(false);
    setRenewStudentData(null);
  };

  const handleRenewSubmit = async () => {
    if (!renewStudentData) return;
    try {
      const newExpiry = addMonthsForRenew(
        renewStudentData.expiryDate,
        Number(renewMonths)
      );
      await updateStudent(token, renewStudentData.id, {
        expiryDate: newExpiry,
      });
      await loadStudents();
      handleCloseRenew();
    } catch (err) {
      console.error("Error renewing student", err);
      setError("Failed to renew student");
    }
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.class : "Unknown Class";
  };

  const getDaysRemaining = (expiryDateStr) => {
    if (!expiryDateStr || isExpired(expiryDateStr)) return 0;
    const expiry = parseDate(expiryDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
      {/* Header Section */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 2, sm: 3, md: 4 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: { xs: 2, sm: 3 },
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          alignItems: { xs: "flex-start", sm: "center" }, 
          justifyContent: "space-between", 
          gap: 2 
        }}>
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ 
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1
              }}
            >
              <Person sx={{ fontSize: { xs: 32, sm: 40 } }} />
              My Students
            </Typography>
            <Typography variant={isMobile ? "body2" : "h6"} sx={{ opacity: 0.9 }}>
              Manage your students and their subscriptions
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            startIcon={<PersonAdd />}
            onClick={() => setAddOpen(true)}
            sx={{
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: "bold",
              px: { xs: 2, sm: 3 },
              py: 1.5,
              "&:hover": {
                background: "rgba(255,255,255,0.3)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
              },
              transition: "all 0.3s ease",
              minWidth: { xs: "100%", sm: "auto" }
            }}
          >
            Add New Student
          </Button>
        </Box>
      </Paper>

      {/* Stats Section */}
      <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              textAlign: "center",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <Person sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, mb: 1 }} />
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
              {students.length}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"}>Total Students</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              textAlign: "center",
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              color: "white",
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <CheckCircle sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, mb: 1 }} />
            <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
              {students.filter(s => !isExpired(s.expiryDate)).length}
            </Typography>
            <Typography variant={isMobile ? "body1" : "h6"}>Active Students</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            fontSize: { xs: '0.8rem', sm: '0.9rem' }
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={isMobile ? 40 : 60} sx={{ color: theme.palette.primary.main }} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && students.length === 0 && (
        <Paper
          sx={{
            p: { xs: 4, sm: 6 },
            textAlign: "center",
            background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            color: "white",
            borderRadius: { xs: 2, sm: 3 },
          }}
        >
          <Person sx={{ fontSize: { xs: 48, sm: 56, md: 64 }, mb: 2, opacity: 0.8 }} />
          <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
            No Students Found
          </Typography>
          <Typography variant={isMobile ? "body2" : "body1"} sx={{ mb: 3, opacity: 0.9 }}>
            Get started by adding your first student
          </Typography>
          <Button
            variant="contained"
            size={isMobile ? "small" : "medium"}
            startIcon={<PersonAdd />}
            onClick={() => setAddOpen(true)}
            sx={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: "bold",
              px: { xs: 2, sm: 3 },
              "&:hover": {
                background: "rgba(255,255,255,0.3)",
              }
            }}
          >
            Add First Student
          </Button>
        </Paper>
      )}

      {/* Students Grid */}
      {!loading && students.length > 0 && (
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {students.map((student) => {
            const isStudentExpired = isExpired(student.expiryDate);
            const daysRemaining = getDaysRemaining(student.expiryDate);
            
            return (
              <Grid item xs={12} sm={6} lg={4} key={student.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.3s ease",
                    border: `1px solid ${isStudentExpired ? theme.palette.error.light : theme.palette.success.light}`,
                    borderRadius: 2,
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <CardContent sx={{ flex: 1, p: { xs: 1.5, sm: 2, md: 3 } }}>
                    {/* Student Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: { xs: 40, sm: 48 },
                          height: { xs: 40, sm: 48 },
                          background: isStudentExpired 
                            ? "linear-gradient(45deg, #FF6B6B, #FF8E8E)"
                            : "linear-gradient(45deg, #4CAF50, #66BB6A)",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        {student.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant={isMobile ? "subtitle1" : "h6"}
                          fontWeight="bold"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {student.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {student.id}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Student Details */}
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.main' }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {student.phone || "No phone"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ClassIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: 'secondary.main' }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {getClassName(student.class_id)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday sx={{ fontSize: { xs: 16, sm: 18 }, color: 'warning.main' }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {student.expiryDate || "No expiry date"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Visibility sx={{ fontSize: { xs: 16, sm: 18 }, color: 'info.main' }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          Content: {student.content === 1 ? "All Content" : "Class Only"}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Status Section */}
                    <Box sx={{ mt: 3 }}>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          label={student.content === 1 ? "All Content" : "Class Only"}
                          size="small"
                          color={student.content === 1 ? "primary" : "default"}
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                        <Chip
                          label={isStudentExpired ? "Expired" : "Active"}
                          size="small"
                          color={isStudentExpired ? "error" : "success"}
                          icon={isStudentExpired ? <Warning /> : <CheckCircle />}
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                        {!isStudentExpired && daysRemaining > 0 && (
                          <Chip
                            label={`${daysRemaining} days left`}
                            size="small"
                            color="warning"
                            icon={<AccessTime />}
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </CardContent>

                  <Divider />

                  {/* Actions */}
                  <CardActions sx={{ 
                    justifyContent: 'space-between', 
                    p: { xs: 1, sm: 1.5, md: 2 },
                    gap: 1 
                  }}>
                    <Tooltip title="Edit Student">
                      <IconButton
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenEdit(student)}
                        sx={{
                          background: theme.palette.primary.light + '20',
                          "&:hover": {
                            background: theme.palette.primary.light + '40',
                          },
                        }}
                      >
                        <Edit fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Renew Subscription">
                      <Button
                        size={isMobile ? "small" : "medium"}
                        startIcon={<Autorenew />}
                        onClick={() => handleOpenRenew(student)}
                        variant={isStudentExpired ? "contained" : "outlined"}
                        color={isStudentExpired ? "error" : "secondary"}
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: '0.7rem', sm: '0.8rem' },
                          px: { xs: 1, sm: 2 },
                          minWidth: { xs: 'auto', sm: 120 }
                        }}
                      >
                        {isMobile ? "Renew" : "Renew Now"}
                      </Button>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* EDIT MODAL */}
      <Dialog 
        open={editOpen} 
        onClose={handleCloseEdit} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white"
        }}>
          Edit Student
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={editForm.name}
            onChange={handleEditChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              ),
            }}
            size={isMobile ? "small" : "medium"}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Phone"
            name="phone"
            value={editForm.phone}
            onChange={handleEditChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
            size={isMobile ? "small" : "medium"}
          />
          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
            <InputLabel id="class-label">Class</InputLabel>
            <Select
              labelId="class-label"
              label="Class"
              name="class_id"
              value={editForm.class_id}
              onChange={handleEditChange}
              startAdornment={
                <InputAdornment position="start">
                  <ClassIcon />
                </InputAdornment>
              }
            >
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.class}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
            <InputLabel id="content-label">Display Content</InputLabel>
            <Select
              labelId="content-label"
              label="Display Content"
              name="content"
              value={editForm.content}
              onChange={handleEditChange}
            >
              <MenuItem value={0}>Only Class Content</MenuItem>
              <MenuItem value={1}>All Content</MenuItem>
            </Select>   
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseEdit} 
            size={isMobile ? "medium" : "large"}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleEditSubmit}
            size={isMobile ? "medium" : "large"}
            fullWidth={isMobile}
            sx={{
              background: "linear-gradient(45deg, #4CAF50, #45a049)",
              fontWeight: "bold",
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* RENEW MODAL */}
      <Dialog 
        open={renewOpen} 
        onClose={handleCloseRenew} 
        fullWidth 
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
          color: "white"
        }}>
          Renew Subscription
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {renewStudentData?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current expiry: {renewStudentData?.expiryDate ? renewStudentData.expiryDate : "N/A"}
            </Typography>
            {isExpired(renewStudentData?.expiryDate) && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Subscription has expired
              </Alert>
            )}
          </Box>

          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
            <InputLabel id="renew-months-label">Extend By</InputLabel>
            <Select
              labelId="renew-months-label"
              label="Extend By"
              value={renewMonths}
              onChange={(e) => setRenewMonths(e.target.value)}
            >
              <MenuItem value={1}>1 month</MenuItem>
              <MenuItem value={3}>3 months</MenuItem>
              <MenuItem value={6}>6 months</MenuItem>
              <MenuItem value={12}>12 months</MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            New expiry date: {renewStudentData && 
              addMonthsForRenew(renewStudentData.expiryDate, Number(renewMonths))}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseRenew} 
            size={isMobile ? "medium" : "large"}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleRenewSubmit}
            size={isMobile ? "medium" : "large"}
            fullWidth={isMobile}
            sx={{
              background: "linear-gradient(45deg, #FF9800, #F57C00)",
              fontWeight: "bold",
            }}
          >
            Confirm Renewal
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADD STUDENT MODAL */}
      <Dialog 
        open={addOpen} 
        onClose={() => setAddOpen(false)} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white"
        }}>
          Add New Student
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            name="name"
            value={addForm.name}
            onChange={handleAddChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person />
                </InputAdornment>
              ),
            }}
            size={isMobile ? "small" : "medium"}
          />

          <TextField
            fullWidth
            margin="normal"
            label="Phone"
            name="phone"
            value={addForm.phone}
            onChange={handleAddChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
            }}
            size={isMobile ? "small" : "medium"}
          />

          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
            <InputLabel id="add-class-label">Class</InputLabel>
            <Select
              labelId="add-class-label"
              name="class_id"
              label="Class"
              value={addForm.class_id}
              onChange={handleAddChange}
              startAdornment={
                <InputAdornment position="start">
                  <ClassIcon />
                </InputAdornment>
              }
            >
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.class}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
            <InputLabel id="add-content-label">Display Content</InputLabel>
            <Select
              labelId="add-content-label"
              name="content"
              label="Display Content"
              value={addForm.content}
              onChange={handleAddChange}
            >
              <MenuItem value={0}>Only Class Content</MenuItem>
              <MenuItem value={1}>All Content</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Password"
            name="password"
            type="password"
            value={addForm.password}
            onChange={handleAddChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock />
                </InputAdornment>
              ),
            }}
            size={isMobile ? "small" : "medium"}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setAddOpen(false)} 
            size={isMobile ? "medium" : "large"}
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddStudent}
            size={isMobile ? "medium" : "large"}
            fullWidth={isMobile}
            sx={{
              background: "linear-gradient(45deg, #4CAF50, #45a049)",
              fontWeight: "bold",
            }}
          >
            Add Student
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}